import React from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, Avatar, Rating, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';

const ReviewsContainer = styled(Box)(({ theme }) => ({
  padding: '6rem 0',
  backgroundColor: 'white',
  position: 'relative'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: '1rem',
  color: '#1e293b',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem'
  }
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  textAlign: 'center',
  marginBottom: '2rem',
  color: '#64748b',
  maxWidth: '600px',
  margin: '0 auto 2rem auto'
}));

const ReviewCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '24px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s ease',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
  }
}));

const ReviewHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '1.5rem',
  gap: '1rem'
}));

const ReviewerAvatar = styled(Avatar)(({ theme }) => ({
  width: '60px',
  height: '60px',
  backgroundColor: '#3b82f6'
}));

const ReviewerInfo = styled(Box)(({ theme }) => ({
  flex: 1
}));

const ReviewerName = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#1e293b',
  marginBottom: '0.25rem'
}));

const ReviewerRole = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#64748b',
  fontWeight: 500
}));

const ReviewComment = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#374151',
  lineHeight: 1.7,
  fontStyle: 'italic',
  position: 'relative',
  '&::before': {
    content: '"',
    fontSize: '3rem',
    color: '#3b82f6',
    position: 'absolute',
    top: '-1rem',
    left: '-0.5rem',
    fontFamily: 'Georgia, serif'
  }
}));

const RatingContainer = styled(Box)(({ theme }) => ({
  marginBottom: '1.5rem'
}));

const RatingWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  marginBottom: '4rem'
}));

const RatingScore = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#1e293b'
}));

const RatingCount = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#64748b'
}));

const ReviewsSection = ({ reviews, businessName }) => {
  const defaultReviews = [
    {
      name: "Sarah Johnson",
      role: "CEO, TechStart Inc.",
      rating: 5,
      comment: "Exceptional service and outstanding results. Their team exceeded our expectations and delivered exactly what we needed for our business growth.",
      avatar: "https://placehold.co/60x60"
    },
    {
      name: "Michael Chen",
      role: "Marketing Director",
      rating: 5,
      comment: "Professional, reliable, and innovative. Working with them has been a game-changer for our digital presence and customer engagement.",
      avatar: "https://placehold.co/60x60"
    },
    {
      name: "Emily Rodriguez",
      role: "Small Business Owner",
      rating: 4,
      comment: "Great value for money and excellent customer support. They helped us streamline our operations and improve our bottom line significantly.",
      avatar: "https://placehold.co/60x60"
    }
  ];

  const displayReviews = reviews || defaultReviews;

  // Calculate rating
  const rating = (displayReviews.reduce((acc, r) => acc + (r.rating || 5), 0) / displayReviews.length).toFixed(1);
  const reviewCount = displayReviews.length;

  return (
    <ReviewsContainer>
      <Container maxWidth="lg">
        <SectionTitle variant="h2">
          What Our Clients Say
        </SectionTitle>
        <SectionSubtitle variant="body1">
          Don't just take our word for it - hear from our satisfied customers
        </SectionSubtitle>

        <RatingWrapper>
          <RatingScore>{rating}</RatingScore>
          <Rating value={Number(rating)} precision={0.1} readOnly size="large" />
          <RatingCount>({reviewCount} Reviews)</RatingCount>
        </RatingWrapper>

        <Grid container spacing={4}>
          {displayReviews.map((review, index) => (
            <Grid item xs={12} md={4} key={index}>
              <ReviewCard>
                <CardContent sx={{ padding: '2.5rem' }}>
                  <ReviewHeader>
                    <ReviewerAvatar
                      src={review.avatar}
                      alt={review.name}
                    >
                      {review.name.charAt(0)}
                    </ReviewerAvatar>
                    <ReviewerInfo>
                      <ReviewerName variant="h6">
                        {review.name}
                      </ReviewerName>
                      <ReviewerRole variant="body2">
                        {review.role}
                      </ReviewerRole>
                    </ReviewerInfo>
                  </ReviewHeader>
                  <RatingContainer>
                    <Rating value={review.rating} readOnly size="medium" />
                  </RatingContainer>
                  <ReviewComment variant="body1">
                    {review.comment}
                  </ReviewComment>
                </CardContent>
              </ReviewCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </ReviewsContainer>
  );
};

export default ReviewsSection;
